import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  Grid,
  InputAdornment,
  Alert,
  InputLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { AuthContext } from '../../authContext/context';
import { postRequest } from '../../lib/api';
import BreadcrumbsWrapper from '../../components/breadcrumbs';
import Swal from 'sweetalert2';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useForm, Controller } from 'react-hook-form';

// Customer Search Modal Component
function CustomerSearchModal({ show, onHide, authToken, onCustomerAdd, selectedCustomers }) {
  const [searchFields, setSearchFields] = useState({
    title: '',
    forename: '',
    surname: '',
    email: '',
    phone_number: ''
  });
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const debounceRef = React.useRef();

  const fetchCustomerSearch = async () => {
    const isEmpty = Object.values(searchFields).every(v => !v);
    if (isEmpty) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      const res = await postRequest('private/leadsCustomerList', searchFields, authToken);
      setSearchResults(res?.data || []);
    } catch (err) {
      console.error('Error fetching search:', err);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchCustomerSearch, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchFields]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearchFields((prev) => ({ ...prev, [name]: value }));
  };

  const isCustomerAdded = (customerId) => {
    return selectedCustomers.some(c => c._id === customerId);
  };

  const handleCreateCustomer = async () => {
    // Check if at least email or phone is provided
    if (!searchFields.email && !searchFields.phone_number) {
      Swal.fire({
        icon: 'error',
        title: 'Missing Information',
        text: 'Please provide at least an email or phone number to create a customer.'
      });
      return;
    }

    try {
      setCreating(true);
      const payload = {
        title: searchFields.title || '',
        forename: searchFields.forename || '',
        surname: searchFields.surname || '',
        email: searchFields.email || '',
        phone_number: searchFields.phone_number || '',
        dob: '',
        address1: '',
        address2: '',
        town: '',
        country: '',
        postcode: ''
      };

      const res = await postRequest('private/createCustomer', payload, authToken);

      if (res?.status === 200 && res?.data) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Customer created successfully!',
          timer: 1500,
          showConfirmButton: false
        });

        // Add the newly created customer
        onCustomerAdd(res.data);

        // Reset search fields
        setSearchFields({
          title: '',
          forename: '',
          surname: '',
          email: '',
          phone_number: ''
        });

        // Close modal
        onHide();
      } else {
        throw new Error(res?.message || 'Failed to create customer');
      }
    } catch (err) {
      console.error('Error creating customer:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || err.message || 'Failed to create customer'
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Add Customer</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className='m-0'>
          {['title', 'forename', 'surname', 'email', 'phone_number'].map((field, idx) => (
            <Col xs={field === 'email' || field === 'phone_number' ? 6 : 4} key={idx}>
              <Box sx={{ '& > :not(style)': { m: 1, width: '100%' } }}>
                <TextField
                  label={field.charAt(0).toUpperCase() + field.slice(1).replace('_', ' ')}
                  name={field}
                  value={searchFields[field]}
                  onChange={handleChange}
                  variant="outlined"
                  type={field === 'email' ? 'email' : field === 'phone_number' ? 'tel' : 'text'}
                />
              </Box>
            </Col>
          ))}
        </Row>

        <Row className='mt-4'>
          <Col>
            <Table responsive bordered>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Add</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="4" className='text-center'>Loading...</td></tr>
                ) : searchResults.length > 0 ? (
                  searchResults.map((customer) => (
                    <tr key={customer._id}>
                      <td>{customer.title} {customer.forename} {customer.surname}</td>
                      <td>{customer.email}</td>
                      <td>{customer.phone_number}</td>
                      <td>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={isCustomerAdded(customer._id)}
                          onClick={() => {
                            onCustomerAdd(customer);
                            onHide();
                          }}
                        >
                          {isCustomerAdded(customer._id) ? 'Added' : 'Add'}
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className='text-center'>
                      <Box sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                          No customers found
                        </Typography>
                        {(searchFields.email || searchFields.phone_number || searchFields.forename || searchFields.surname) && (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleCreateCustomer}
                            disabled={creating}
                            sx={{
                              backgroundColor: '#6f42c1',
                              '&:hover': {
                                backgroundColor: '#5a32a3'
                              }
                            }}
                          >
                            {creating ? 'Creating...' : 'Add Customer'}
                          </Button>
                        )}
                      </Box>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

export default function MortgageRedemptionDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // Support both 'customerId' and 'customer_id' parameter names
  const customerIdFromUrl = searchParams.get('customerId') || searchParams.get('customer_id');
  const authCtx = useContext(AuthContext);

  // Get basicFormData from previous step (SimpleConveyancingForm)
  const basicFormData = location.state?.basicFormData || {};
  console.log('📋 Basic Form Data from Step 1 (Mortgage Redemption):', basicFormData);
  
  const [conveyancingData, setConveyancingData] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lenderPanels, setLenderPanels] = useState([]);
  const [partners, setPartners] = useState([]);
  const [isPostcodeValid, setIsPostcodeValid] = useState(true); // Initially true for edit page (existing data)
  const [postcodeValue, setPostcodeValue] = useState('');
  const [checkingPostcode, setCheckingPostcode] = useState(false);
  const [postcodeMatch, setPostcodeMatch] = useState(null);
  const [availableBranches, setAvailableBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue
  } = useForm({
    defaultValues: {
      // Property Details
      line1: '',
      line2: '',
      town: '',
      county: '',
      postcode: '',
      propertyValue: '',
      lender_panel_id: '',
      partner_id: '',

      // Info section
      ownershipType: ''
    }
  });

  // Ownership type options
  const ownershipTypes = [
    { value: 'freehold', label: 'Freehold' },
    { value: 'leasehold', label: 'Leasehold' },
    //{ value: 'share_of_freehold', label: 'Share of Freehold' }
  ];

  // Fetch active lender panels
  const fetchActiveLenderPanels = async () => {
    try {
      const response = await postRequest('private/getActiveLenderPanels', {}, authCtx.token);
      if (response?.status === 200 && response?.data) {
        setLenderPanels(response.data);
      }
    } catch (error) {
      console.error('Error fetching lender panels:', error);
    }
  };

  // Fetch partners
  const fetchPartners = async () => {
    try {
      const response = await postRequest('private/partnerList', { page: 1, limit: 100 }, authCtx.token);
      if (response?.status === 200 && response?.data?.docs) {
        setPartners(response.data.docs);
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
    }
  };

  // Fetch conveyancing details
  const fetchConveyancingDetails = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching Mortgage Redemption data for:', id);

      // Use get-single-lead API (same as view page)
      const res = await postRequest(`private/get-single-lead/${id}`, {}, authCtx.token);

      if ((res?.status === 200 || res?.status === 'success') && res?.data) {
        const leadData = res.data;
        setConveyancingData(leadData);

        console.log('✅ Lead data loaded:', leadData);
        console.log('📦 Property Details:', leadData.propertyDetails);
        console.log('👥 Customers:', leadData.customers);
        console.log('👥 Customers Length:', leadData.customers?.length);
        console.log('👥 Customers Type:', typeof leadData.customers);
        console.log('👥 Is Array:', Array.isArray(leadData.customers));
        if (leadData.customers && leadData.customers.length > 0) {
          console.log('👤 First Customer Full:', JSON.stringify(leadData.customers[0], null, 2));
        }

        // Populate form with existing data
        if (leadData.propertyDetails) {
          // Handle address
          if (leadData.propertyDetails.address) {
            setValue('line1', leadData.propertyDetails.address.line1 || '');
            setValue('line2', leadData.propertyDetails.address.line2 || '');
            setValue('town', leadData.propertyDetails.address.town || '');
            setValue('county', leadData.propertyDetails.address.county || '');
            const prefilledPostcode = leadData.propertyDetails.address.postcode || '';
            setValue('postcode', prefilledPostcode);
            setPostcodeValue(prefilledPostcode); // trigger postcode validation for prefilled value
          }

          // Handle property value
          setValue('propertyValue', leadData.propertyDetails.propertyValue || '');
        } else {
          // Fallback to flat fields if propertyDetails doesn't exist
          console.log('⚠️ propertyDetails not found, using flat fields');

          // Set address fields - check multiple possible structures
          if (leadData.property_address) {
            setValue('line1', leadData.property_address.line1 || '');
            setValue('line2', leadData.property_address.line2 || '');
            setValue('town', leadData.property_address.town || '');
            setValue('county', leadData.property_address.county || '');
            const prefilledPostcode = leadData.property_address.postcode || '';
            setValue('postcode', prefilledPostcode);
            setPostcodeValue(prefilledPostcode); // trigger postcode validation for prefilled value
          } else if (leadData.address) {
            // Handle legacy address structure with address1/address2/country
            setValue('line1', leadData.address.address1 || '');
            setValue('line2', leadData.address.address2 || '');
            setValue('town', leadData.address.town || '');
            setValue('county', leadData.address.country || ''); // country maps to county
            const prefilledPostcode = leadData.address.postcode || '';
            setValue('postcode', prefilledPostcode);
            setPostcodeValue(prefilledPostcode); // trigger postcode validation for prefilled value
          }

          // Set property value from flat field
          setValue('propertyValue', leadData.property_value || '');
        }

        // Handle info section (ownership type)
        let ownershipType = 'freehold'; // Default
        if (leadData.info && leadData.info.ownershipType) {
          ownershipType = leadData.info.ownershipType.toLowerCase();
        } else if (leadData.ownership_type) {
          ownershipType = leadData.ownership_type.toLowerCase();
        }
        setValue('ownershipType', ownershipType);
        console.log('🏘️ Setting ownership type:', ownershipType);

        // Handle lender panel
        if (leadData.lender_panel_id) {
          const lenderPanelValue = typeof leadData.lender_panel_id === 'object' ? leadData.lender_panel_id._id : leadData.lender_panel_id;
          setValue('lender_panel_id', lenderPanelValue);
          console.log('🏦 Setting lender panel ID:', lenderPanelValue);
        }

        // Partner
        const partnerId = leadData.partner_id
          ? (typeof leadData.partner_id === 'object' ? leadData.partner_id._id : leadData.partner_id)
          : '';
        console.log('🤝 Partner ID:', partnerId);
        if (partnerId) {
          setValue('partner_id', String(partnerId));
        }

        // Set customers from lead data - prioritize DB data over URL parameter
        console.log('🔍 Checking customer conditions:', {
          hasCustomers: !!leadData.customers,
          customersLength: leadData.customers?.length,
          customerIdFromUrl: customerIdFromUrl,
          currentCustomersState: customers.length
        });

        if (leadData.customers && leadData.customers.length > 0) {
          console.log('👥 Processing customers from lead data:', leadData.customers);

          // Check if customers are already populated (objects) or just IDs
          if (Array.isArray(leadData.customers) && leadData.customers.length > 0) {
            const firstCustomer = leadData.customers[0];
            console.log('👤 First customer:', firstCustomer);
            console.log('👤 Type:', typeof firstCustomer);
            console.log('👤 Has _id:', !!firstCustomer._id);
            console.log('👤 Has customer_id:', !!firstCustomer.customer_id);

            if (typeof firstCustomer === 'object' && firstCustomer !== null && (firstCustomer._id || firstCustomer.customer_id)) {
              // Already populated objects
              console.log('✅ Setting customers to state (populated objects)');
              setCustomers(leadData.customers);
              console.log('✅ Customers state set with', leadData.customers.length, 'customers');
            } else if (typeof firstCustomer === 'string') {
              // Customers are just IDs - fetch details
              console.log('🔍 Customers are IDs, fetching details...');

              const customerPromises = leadData.customers.map(async (customerId) => {
                try {
                  console.log('📤 Fetching customer:', customerId);
                  const res = await postRequest(
                    `private/getCustomerIndividualData/${customerId}`,
                    {},
                    authCtx.token
                  );
                  if (res?.data) {
                    console.log('✅ Fetched customer:', res.data);
                    return res.data;
                  }
                  console.log('⚠️ No data for customer:', customerId);
                  return null;
                } catch (err) {
                  console.error('❌ Error fetching customer:', customerId, err);
                  return null;
                }
              });

              Promise.all(customerPromises).then(fetchedCustomers => {
                const validCustomers = fetchedCustomers.filter(c => c !== null);
                console.log('✅ All customers fetched, setting state with', validCustomers.length, 'customers');
                setCustomers(validCustomers);
              });
            } else {
              console.log('⚠️ Customer format not recognized:', typeof firstCustomer, firstCustomer);
            }
          } else {
            console.log('⚠️ Customers is not an array or is empty');
          }
        } else {
          console.log('⚠️ Skipping customer processing:', {
            noCustomers: !leadData.customers,
            hasUrlParam: !!customerIdFromUrl
          });
        }
      }
    } catch (error) {
      console.error('❌ Error fetching Mortgage Redemption details:', error);
      Swal.fire('Error', 'Failed to load lead data', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id && id !== 'new' && authCtx.token) {
      fetchConveyancingDetails();
    }
  }, [id, authCtx.token]);

  // Fetch initial customer from URL parameter (only if creating new lead without existing customers)
  useEffect(() => {
    const fetchInitialCustomer = async () => {
      // Only use URL param if we don't have lead data yet (creating new lead)
      if (customerIdFromUrl && authCtx.token && customers.length === 0 && !conveyancingData) {
        console.log('📌 Fetching customer from URL parameter:', customerIdFromUrl);
        try {
          // Use same endpoint as Purchase component (supports both MongoDB ID and custom ID)
          const customerRes = await postRequest(
            `private/getCustomerIndividualData/${customerIdFromUrl}`,
            {},
            authCtx.token
          );
          if (customerRes?.data) {
            console.log('✅ URL param customer fetched:', customerRes.data);
            setCustomers([customerRes.data]);
          }
        } catch (error) {
          console.error('❌ Error fetching initial customer:', error);
        }
      } else {
        console.log('⏭️ Skipping URL param customer fetch - using lead data instead');
      }
    };
    fetchInitialCustomer();
  }, [customerIdFromUrl, authCtx.token, conveyancingData]);

  // Fetch active lender panels on mount
  useEffect(() => {
    fetchActiveLenderPanels();
    fetchPartners();
  }, [authCtx.token]);

  const handleAddCustomer = (customer) => {
    if (!customers.some(c => c._id === customer._id)) {
      setCustomers([...customers, customer]);
    }
  };

  const handleRemoveCustomer = (customerId) => {
    setCustomers(customers.filter(c => c._id !== customerId));
  };

  // Validate postcode using backend proxy API (avoids CORS)
  // 2-Step Postcode Validation
  useEffect(() => {
    if (!postcodeValue || postcodeValue.trim().length === 0) {
      setIsPostcodeValid(false);
      return;
    }

    const checkPostcode = async () => {
      setCheckingPostcode(true);
      try {
        setPostcodeMatch(null);
        setAvailableBranches([]);
        setSelectedBranch(null);
        setIsPostcodeValid(false);

        // STEP 1: Check if postcode exists
        const checkRes = await postRequest(
          'private/checkPostcodeInBranch',
          { postcode: postcodeValue.trim() },
          authCtx.token
        );

        if (checkRes?.status === 406 || checkRes?.data?.exists === false) {
          setIsPostcodeValid(false);
          Swal.fire({
            title: 'Invalid Postcode',
            text: checkRes?.message || 'This postcode does not exist in the UK postcode database. Please enter a valid UK postcode.',
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#6f42c1'
          });
          return;
        }

        // STEP 2: Match branch by postcode
        const matchRes = await postRequest(
          'private/matchBranchByPostcode',
          { postcode: postcodeValue.trim() },
          authCtx.token
        );

        if (matchRes?.data?.matched) {
          setPostcodeMatch(matchRes.data.branch);
          setIsPostcodeValid(true);
        } else {
          setIsPostcodeValid(false);
          Swal.fire({
            title: 'No Branch Coverage',
            text: 'This postcode area is not currently served by any of our branches.',
            icon: 'warning',
            confirmButtonText: 'OK',
            confirmButtonColor: '#6f42c1'
          });
        }
      } catch (error) {
        console.error('Error checking postcode:', error);
        setIsPostcodeValid(false);
      } finally {
        setCheckingPostcode(false);
      }
    };

    const timeoutId = setTimeout(checkPostcode, 500);
    return () => clearTimeout(timeoutId);
  }, [postcodeValue, authCtx.token]);

  const onSubmit = async (formData) => {

    try {
      console.log('📝 Form Data Submitted:', formData);
      console.log('🏘️ Ownership Type from form:', formData.ownershipType);
      console.log('👥 Customers State:', customers);
      console.log('👥 Number of customers:', customers.length);

      if (customers.length === 0) {
        Swal.fire({
          icon: 'warning',
          title: 'No Customers',
          text: 'Please add at least one customer before saving.'
        });
        return;
      }

      // Block save until both postcode checks (UK database + branch match) have passed
      if (checkingPostcode) {
        Swal.fire({
          icon: 'info',
          title: 'Please Wait',
          text: 'Postcode verification is still in progress. Please try again in a moment.'
        });
        return;
      }

      if (!isPostcodeValid) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid Postcode',
          text: 'Please enter a valid UK postcode that is covered by one of our branches before saving.'
        });
        return;
      }

      const payload = {
        // Data from Step 1 (SimpleConveyancingForm)
        override_date: basicFormData.override_date || new Date(),
        group_code: basicFormData.group_code,
        acquisition: basicFormData.acquisition || 'Direct',
        product_id: basicFormData.product_id,
        service_type: basicFormData.service_type || 'Mortgage_Redemption',

        customer_ids: customers.map(c => c._id),
        property_address: {
          address1: formData.line1,
          address2: formData.line2,
          town: formData.town,
          country: formData.county,
          postcode: formData.postcode
        },
        property_value: formData.propertyValue,
        ownership_type: formData.ownershipType,
        lender_panel_id: formData.lender_panel_id || null,
        partner_id: formData.partner_id || null
      };

      console.log('📤 Payload being sent to API:', payload);

      const actualLeadId = id === 'new' ? 'new' : id;
      const res = await postRequest(
        `private/saveConveyancingMortgageRedemptionToLead/${actualLeadId}`,
        payload,
        authCtx.token
      );

      console.log('📡 API Response:', res);

      if (res?.status === 'success' || res?.status === 200) {
        const savedLeadId = res?.data?.lead_id || actualLeadId;

        // 🎯 Call unified API to calculate and save to final_leads_calculations
        try {
          // ⏱️ Wait 300ms for database to commit flags before calculating fees
          console.log('⏳ Waiting 300ms for database to commit flags...');
          await new Promise(resolve => setTimeout(resolve, 300));

          console.log('💰 Calling calculateAndSaveLeadQuote for Mortgage Redemption...');

          const feeResponse = await postRequest('private/calculateAndSaveLeadQuote', {
            lead_id: savedLeadId,
            service_type: 'Mortgage_Redemption',
            product_id: payload.product_id,
            property_value: payload.property_value,
            postcode: payload.property_address?.postcode,
            partner_id: payload.partner_id || null,
            ownership_type: payload.ownership_type
          }, authCtx.token);

          console.log('✅ calculateAndSaveLeadQuote response:', feeResponse);
        } catch (feeError) {
          console.error('⚠️ Error calling calculateAndSaveLeadQuote:', feeError);
        }

        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: id === 'new'
            ? 'Mortgage Redemption created and email sent successfully!'
            : 'Mortgage Redemption details updated successfully!'
        }).then(() => {
          navigate(`/leads/view/mortgage-redemption/${savedLeadId}`);
        });
      } else {
        throw new Error(res?.message || 'Failed to save details');
      }
    } catch (error) {
      console.error('Error saving details:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to save details'
      });
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Leads', path: '/leads' },
          { label: 'Edit Conveyancing', path: '' },
        ]}
      />
      <br />

      <Box sx={{ p: 3 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h5" component="h1" sx={{ mb: 3, fontWeight: 'bold' }}>
            Edit Conveyancing #{conveyancingData?.reference || id} - Mortgage Redemption
          </Typography>

          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Mortgage Redemption Details Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#666' }}>
                Mortgage Redemption Details
              </Typography>
              
              {/* Property Section */}
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Property
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Address</Typography>
                  <Controller
                    name="line1"
                    control={control}
                    rules={{ required: 'Address Line 1 is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Line 1"
                        variant="outlined"
                        size="small"
                        error={!!errors.line1}
                        helperText={errors.line1?.message}
                        sx={{ mb: 1 }}
                      />
                    )}
                  />
                  <Controller
                    name="line2"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Line 2"
                        variant="outlined"
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    )}
                  />
                  <Controller
                    name="town"
                    control={control}
                    rules={{ required: 'Town is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Town"
                        variant="outlined"
                        size="small"
                        error={!!errors.town}
                        helperText={errors.town?.message}
                        sx={{ mb: 1 }}
                      />
                    )}
                  />
                  <Controller
                    name="county"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="County"
                        variant="outlined"
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    )}
                  />
                  <Controller
                    name="postcode"
                    control={control}
                    rules={{ required: 'Postcode is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Postcode (e.g. M41 0TZ)"
                        variant="outlined"
                        size="small"
                        error={!!errors.postcode}
                        helperText={errors.postcode?.message}
                        sx={{ mb: 1 }}
                        onChange={(e) => {
                          field.onChange(e);
                          setPostcodeValue(e.target.value);
                        }}
                      />
                    )}
                  />

                  {/* Checking postcode status */}
                  {checkingPostcode && postcodeValue && (
                    <Alert severity="info" sx={{ mt: 1, fontSize: '0.875rem' }}>
                      Checking postcode in branches...
                    </Alert>
                  )}

                  {/* Postcode match found */}
                  {postcodeMatch && !checkingPostcode && (
                    <Alert severity="success" sx={{ mt: 1, fontSize: '0.875rem' }}>
                      ✓ Postcode found in branch: <strong>{postcodeMatch.name}</strong>
                    </Alert>
                  )}

                  {/* Branch selection dropdown when postcode not found */}
                  {!postcodeMatch && availableBranches.length > 0 && !checkingPostcode && (
                    <Box sx={{ mt: 1 }}>
                      <Alert severity="warning" sx={{ mb: 1, fontSize: '0.875rem' }}>
                        Postcode not found in any branch. Please select a branch:
                      </Alert>
                      <FormControl fullWidth size="small">
                        <InputLabel>Select Branch</InputLabel>
                        <Select
                          value={selectedBranch || ''}
                          onChange={(e) => {
                            setSelectedBranch(e.target.value);
                            setIsPostcodeValid(true);
                          }}
                          label="Select Branch"
                        >
                          {availableBranches.map((branch) => (
                            <MenuItem key={branch._id} value={branch._id}>
                              {branch.name} - {branch.address?.postcode}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}

                  <Typography variant="body2" sx={{ mb: 1, mt: 2 }}>Ownership Type</Typography>
                  <Controller
                    name="ownershipType"
                    control={control}
                    rules={{ required: 'Ownership type is required' }}
                    render={({ field }) => (
                      <FormControl fullWidth size="small" error={!!errors.ownershipType}>
                        <Select {...field}>
                          <MenuItem value="">Select Ownership Type</MenuItem>
                          {ownershipTypes.map((type) => (
                            <MenuItem key={type.value} value={type.value}>
                              {type.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.ownershipType && (
                          <Typography variant="caption" color="error">
                            {errors.ownershipType.message}
                          </Typography>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>Property Value</Typography>
                    <Controller
                      name="propertyValue"
                      control={control}
                      rules={{ required: 'Property value is required' }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          variant="outlined"
                          size="small"
                          type="number"
                          slotProps={{
                            input: {
                              startAdornment: <InputAdornment position="start">£</InputAdornment>,
                            }
                          }}
                          error={!!errors.propertyValue}
                          helperText={errors.propertyValue?.message}
                        />
                      )}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Customers Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Customers
              </Typography>

              <Box sx={{ mb: 3 }}>
                {customers.map((customer) => (
                  <Box
                    key={customer._id}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      mb: 2,
                      p: 1.5,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      backgroundColor: '#f9f9f9'
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {customer.title} {customer.forename} {customer.surname}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center' }}>
                          📧 {customer.email}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center' }}>
                          📞 {customer.phone_number}
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      size="small"
                      onClick={() => handleRemoveCustomer(customer._id)}
                      sx={{
                        minWidth: 'auto',
                        color: '#d32f2f',
                        '&:hover': {
                          backgroundColor: '#ffebee'
                        }
                      }}
                    >
                      ✕
                    </Button>
                  </Box>
                ))}
                <Button
                  variant="text"
                  startIcon={<AddIcon />}
                  onClick={() => setShowCustomerModal(true)}
                  sx={{ color: '#6f42c1', mt: 1 }}
                >
                  Add Customer
                </Button>
              </Box>
            </Box>

            {/* Lender Panel Section */}
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
              Lender Panel
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="lender_panel_id"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <Select
                        {...field}
                        displayEmpty
                        sx={{ backgroundColor: 'white' }}
                      >
                        <MenuItem value="">
                          <em>Select Lender Panel (Optional)</em>
                        </MenuItem>
                        {lenderPanels.map((panel) => (
                          <MenuItem key={panel._id} value={panel._id}>
                            {panel.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>

            {/* Partner Section */}
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
              Partner
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="partner_id"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <Select
                        {...field}
                        displayEmpty
                        sx={{ backgroundColor: 'white' }}
                      >
                        <MenuItem value="">
                          <em>Select Partner (Optional)</em>
                        </MenuItem>
                        {partners.map((partner) => (
                          <MenuItem key={partner._id} value={partner._id}>
                            {partner.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>

            {/* Submit Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                variant="contained"
                disabled={checkingPostcode || !isPostcodeValid}
                sx={{
                  backgroundColor: '#6f42c1',
                  '&:hover': {
                    backgroundColor: '#5a32a3'
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#cccccc',
                    color: '#666666'
                  }
                }}
              >
                Save
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>

      {/* Customer Search Modal */}
      <CustomerSearchModal
        show={showCustomerModal}
        onHide={() => setShowCustomerModal(false)}
        authToken={authCtx.token}
        onCustomerAdd={handleAddCustomer}
        selectedCustomers={customers}
      />
    </>
  );
}